import { CheckCircle2, AlertTriangle, XCircle, Info, Circle, Inbox } from "lucide-react";

// Semantic tones — color + icon + text, never color alone.
const TONES = {
  success: { badge: "bg-green-50 text-green-700 border-green-300", banner: "bg-green-50 border-green-300 text-green-800", dot: "text-green-600", icon: CheckCircle2 },
  warning: { badge: "bg-amber-50 text-amber-800 border-amber-300", banner: "bg-amber-50 border-amber-300 text-amber-900", dot: "text-amber-600", icon: AlertTriangle },
  danger: { badge: "bg-red-50 text-red-700 border-red-300", banner: "bg-red-50 border-red-300 text-red-800", dot: "text-red-600", icon: XCircle },
  info: { badge: "bg-sky-50 text-sky-700 border-sky-300", banner: "bg-sky-50 border-sky-300 text-sky-800", dot: "text-sky-600", icon: Info },
  neutral: { badge: "bg-slate-100 text-slate-600 border-slate-300", banner: "bg-slate-50 border-slate-300 text-slate-700", dot: "text-slate-500", icon: Circle },
};

export function SectionCard({ title, subtitle, icon: Icon, actions, children, className = "", testid }) {
  return (
    <div className={`section-card ${className}`} data-testid={testid}>
      {(title || actions) && (
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-border">
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-head font-semibold text-[15px] text-foreground">
              {Icon && <Icon size={16} className="text-navy-700 shrink-0" strokeWidth={2} />}
              {title}
            </div>
            {subtitle && <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>}
          </div>
          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </div>
      )}
      <div className="p-4">{children}</div>
    </div>
  );
}

export function StatusBadge({ tone = "neutral", children, icon = true, className = "", testid }) {
  const t = TONES[tone] || TONES.neutral;
  const Icon = t.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 border rounded-md ${t.badge} ${className}`} data-testid={testid}>
      {icon && <Icon size={12} strokeWidth={2.2} />}
      {children}
    </span>
  );
}

export function AlertBanner({ tone = "info", title, children, icon: Override, className = "", testid }) {
  const t = TONES[tone] || TONES.info;
  const Icon = Override || t.icon;
  return (
    <div className={`flex items-start gap-2.5 border rounded-lg px-4 py-2.5 ${t.banner} ${className}`} data-testid={testid}>
      <Icon size={17} strokeWidth={2} className="mt-0.5 shrink-0" />
      <div className="min-w-0 text-sm">
        {title && <div className="font-head font-semibold">{title}</div>}
        {children && <div className={title ? "text-[13px] opacity-90 mt-0.5" : ""}>{children}</div>}
      </div>
    </div>
  );
}

export function MetricCard({ label, value, tone, icon: Icon, hint, testid }) {
  const alert = tone && tone !== "neutral";
  const t = tone ? (TONES[tone] || TONES.neutral) : null;
  return (
    <div className={`bg-card border rounded-lg shadow-card p-3 ${alert ? t.badge.replace(/text-\S+/, "").replace(/bg-\S+/, "") + " border-current/30" : "border-border"}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className={`text-xl font-head font-bold tabular ${alert ? t.dot : "text-navy-700"}`}>{value}</div>
        {Icon && <Icon size={16} className={alert ? t.dot : "text-muted-foreground"} />}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, description, action, testid }) {
  return (
    <div className="section-card p-10 text-center" data-testid={testid}>
      <Icon size={38} className="mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
      <div className="font-head text-base font-semibold text-foreground">{title}</div>
      {description && <p className="text-sm text-muted-foreground max-w-md mx-auto mt-1">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export { TONES };
